import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { BlockRenderer } from "@/components/builder/BlockRenderer";
import { Phone, Mail, MapPin } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { PublicTerminologyProvider, usePublicTerminology } from "@/lib/terminology";
import { getPublicCardStyle } from "@/lib/contrast";
import { useDocumentSeo, seoFromPage } from "@/lib/seo";
import TrackingScripts from "@/components/TrackingScripts";

export default function PublicSalon() {
  const { slug, pageSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/salon/${slug}`).then(({ data }) => setData(data)).catch((e) => setError(formatApiError(e.response?.data?.detail) || "Salon nicht gefunden"));
  }, [slug]);

  if (error) return <div className="min-h-screen flex items-center justify-center"><div><p className="text-2xl font-display mb-2">{error}</p><Link to="/login" className="mv-btn">Zur Anmeldung</Link></div></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ color: "var(--mv-text-muted)" }}>Lade Salon ...</div>;

  const { salon, theme, header, footer, pages, blog, galleries, forms, services } = data;
  const staff = data.staff || [];
  const page = pageSlug ? pages.find((p) => p.slug === pageSlug) : pages.find((p) => p.is_home) || pages[0];

  const submitForm = async (formId, values) => {
    try {
      await api.post(`/public/salon/${slug}/form/${formId}/submit`, values);
      return true;
    } catch { return false; }
  };

  return (
    <PublicTerminologyProvider terms={data.terminology || {}} industry={data.industry || null}>
      <PublicSalonInner
        data={data}
        salon={salon}
        theme={theme}
        header={header}
        footer={footer}
        pages={pages}
        blog={blog}
        galleries={galleries}
        forms={forms}
        services={services}
        staff={staff}
        page={page}
        pageSlug={pageSlug}
        slug={slug}
        navigate={navigate}
        submitForm={submitForm}
      />
    </PublicTerminologyProvider>
  );
}

function PublicSalonInner({ data, salon, theme, header, footer, pages, blog, galleries, forms, services, staff, page, pageSlug, slug, navigate, submitForm }) {
  // Apply SEO only for "regular" page routes here. Special routes (booking,
  // blog list, blog post) own their SEO inside their dedicated components
  // below — passing skip:true here prevents the parent effect from racing
  // with and overwriting the child's tags.
  const isSpecialRoute = pageSlug === "booking" || pageSlug === "blog" || (pageSlug && pageSlug.startsWith("blog/"));
  useDocumentSeo(isSpecialRoute ? {} : seoFromPage({ page, salon, header }));
  if (pageSlug === "blog") return <BlogList salon={salon} blog={blog} theme={theme} header={header} footer={footer} planFeatures={data.plan_features || []} />;
  if (pageSlug === "booking") return <BookingPage salon={salon} services={services || []} staff={data.staff || []} addons={data.addons || []} categories={data.service_categories || []} staffBlocks={data.staff_blocks || []} settings={data.settings || {}} theme={theme} header={header} footer={footer} planFeatures={data.plan_features || []} />;
  if (pageSlug && pageSlug.startsWith("blog-") || (pageSlug === "post" && pages.find)) {
    // handled separately below for blog post
  }

  // Blog post route: /salon/:slug/blog/:postSlug handled by separate component below

  if (!page) return <div className="min-h-screen flex items-center justify-center">Seite nicht gefunden</div>;

  return (
    <PublicShell salon={salon} theme={theme} header={header} footer={footer} planFeatures={data.plan_features || []}>
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-8 lg:py-16" style={{ fontFamily: `'${theme?.body_font || "Manrope"}', sans-serif` }}>
        {(page.blocks || []).map((b) => (
          <div key={b.id}>
            <BlockRenderer block={b} theme={theme} forms={forms} services={services || []} staff={staff || []} addons={data.addons || []} categories={data.service_categories || []} salonSlug={slug} loyalty={data.loyalty} planFeatures={data.plan_features || []} onSubmitForm={submitForm} />
          </div>
        ))}
      </div>
    </PublicShell>
  );
}

export function PublicShell({ salon, theme, header, footer, children, planFeatures = [] }) {
  // A salon's "Newsletter-Modul" is part of higher-tier plans. When the salon
  // downgraded (or never had it), we must NOT show the footer newsletter form
  // even if `footer.newsletter_enabled` is still true in the saved doc.
  const newsletterAllowed = (planFeatures || []).includes("newsletter");
  const bookingAllowed = (planFeatures || []).includes("bookings");
  const bookingHref = `/salon/${salon.slug}/booking`;
  const { t } = usePublicTerminology();
  const primary = theme?.primary_color || "#C87A5F";
  const text = theme?.text_color || "#2C2621";
  const bg = theme?.bg_color || "#FDFBF7";
  const heading = theme?.heading_font || "Playfair Display";
  const body = theme?.body_font || "Manrope";

  return (
    <div style={{ background: bg, color: text, fontFamily: `'${body}', sans-serif` }} className="min-h-screen">
      <TrackingScripts gaId={theme?.ga_measurement_id} metaPixelId={theme?.meta_pixel_id} />
      <header className="sticky top-0 z-40 border-b" style={{ borderColor: "rgba(0,0,0,0.08)", background: `${bg}cc`, backdropFilter: "blur(20px)" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to={`/salon/${salon.slug}`} className="flex items-center gap-2">
            {header?.logo_url ? <img src={fileUrl(header.logo_url)} className="h-9 object-contain" /> : <span style={{ fontFamily: `'${heading}', serif` }} className="text-xl font-medium">{salon.name}</span>}
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            {(header?.menu_links || []).map((l, i) => (
              <Link key={i} to={l.url === "/" ? `/salon/${salon.slug}` : `/salon/${salon.slug}${l.url}`} className="hover:opacity-70 transition-opacity">{l.label}</Link>
            ))}
            <Link to={`/salon/${salon.slug}/blog`} className="hover:opacity-70">{t("blog")}</Link>
            <Link to={`/salon/${salon.slug}/account`} className="hover:opacity-70" data-testid="customer-portal-link">{t("account")}</Link>
          </nav>
          <div className="flex items-center gap-3">
            {header?.phone && <a href={`tel:${header.phone}`} className="hidden md:flex text-sm items-center gap-1.5"><Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {header.phone}</a>}
            {header?.cta_label && (() => {
              const ctaHref = header.cta_url && header.cta_url !== "#" ? header.cta_url : bookingHref;
              if (ctaHref === bookingHref && !bookingAllowed) return null;
              return <a href={ctaHref} className="px-4 py-2 rounded-full text-white text-sm" style={{ background: primary }}>{header.cta_label}</a>;
            })()}
          </div>
        </div>
      </header>
      <main>{children}</main>
      <footer className="border-t mt-20" style={{ borderColor: "rgba(0,0,0,0.08)", background: bg === "#FDFBF7" ? "#F4F1EB" : bg }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <p style={{ fontFamily: `'${heading}', serif` }} className="text-xl mb-3">{salon.name}</p>
            <p className="text-sm opacity-75">{footer?.address}</p>
          </div>
          <div>
            <p className="mv-label mb-3">Kontakt</p>
            {footer?.phone && <p className="text-sm flex items-center gap-1.5 mb-1"><Phone className="w-3 h-3" strokeWidth={1.5} /> {footer.phone}</p>}
            {footer?.email && <p className="text-sm flex items-center gap-1.5 mb-1"><Mail className="w-3 h-3" strokeWidth={1.5} /> {footer.email}</p>}
            {footer?.address && <p className="text-sm flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5" strokeWidth={1.5} /> <span>{footer.address}</span></p>}
          </div>
          <div>
            <p className="mv-label mb-3">Öffnungszeiten</p>
            {(footer?.opening_hours || []).map((h, i) => (
              <p key={i} className="text-sm flex justify-between"><span>{h.day}</span><span className="opacity-75">{h.hours}</span></p>
            ))}
          </div>
          <div>
            <p className="mv-label mb-3">Rechtliches</p>
            {(footer?.legal_links || []).map((l, i) => {
              // Normalize URL: if it's a relative path like "/impressum", scope it to the salon.
              // External URLs (http/https/mailto) stay as-is.
              const raw = l.url || "";
              const isExternal = /^(https?:|mailto:|tel:)/.test(raw);
              const href = isExternal
                ? raw
                : `/salon/${salon.slug}/${raw.replace(/^\/+/, "")}`;
              return (
                <a key={i} href={href} className="text-sm block opacity-75 hover:opacity-100">{l.label}</a>
              );
            })}
            <button
              type="button"
              onClick={() => window.__openCookieConsent && window.__openCookieConsent()}
              className="text-sm block opacity-75 hover:opacity-100 underline-offset-2 hover:underline text-left"
              data-testid="footer-cookie-settings-salon"
            >
              Cookie-Einstellungen
            </button>
          </div>
        </div>
        {footer?.newsletter_enabled && newsletterAllowed && (
          <div className="border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }} data-testid="footer-newsletter-section">
            <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
              <FooterNewsletter primary={primary} heading={heading} slug={salon.slug} />
            </div>
          </div>
        )}
        <div className="border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-12 py-4 text-xs opacity-60 flex justify-between">
            <span>{footer?.copyright_text || `© ${new Date().getFullYear()} ${salon.name}`}</span>
            <span>Powered by StudioMV</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function BlogList({ salon, blog, theme, header, footer, planFeatures = [] }) {
  const { t } = usePublicTerminology();
  const heading = theme?.heading_font || "Playfair Display";
  const salonName = (header && header.brand_name) || (salon && salon.name) || "";
  useDocumentSeo({
    title: `${t("blog")}${salonName ? ` | ${salonName}` : ""}`,
    description: `Aktuelle Beiträge, Trends und News${salonName ? ` von ${salonName}` : ""}.`,
    ogImage: header?.logo_url || null,
  });
  return (
    <PublicShell salon={salon} theme={theme} header={header} footer={footer} planFeatures={planFeatures}>
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
        <p className="mv-label mb-2">Magazin</p>
        <h1 style={{ fontFamily: `'${heading}', serif` }} className="text-5xl mb-12">{t("blog")}</h1>
        <div className="grid md:grid-cols-2 gap-8">
          {blog.map((p) => (
            <Link key={p.id} to={`/salon/${salon.slug}/blog/${p.slug}`} className="block group">
              {p.cover_image && <img src={p.cover_image} className="w-full h-64 object-cover rounded-xl mb-4 group-hover:scale-[1.01] transition-transform" />}
              <p className="text-xs mb-1" style={{ color: theme?.primary_color || "#C87A5F" }}>{p.category}</p>
              <h3 style={{ fontFamily: `'${heading}', serif` }} className="text-2xl mb-2">{p.title}</h3>
              <p className="text-sm opacity-75">{p.excerpt}</p>
            </Link>
          ))}
        </div>
      </div>
    </PublicShell>
  );
}

export function BlogPostPage() {
  const { slug, postSlug } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/public/salon/${slug}`).then(({ data }) => setData(data)); }, [slug]);
  const post = data?.blog?.find((p) => p.slug === postSlug);
  const salonName = (data?.header?.brand_name) || (data?.salon?.name) || "";
  useDocumentSeo({
    title: post ? `${post.title}${salonName ? ` | ${salonName}` : ""}` : "",
    description: post?.excerpt || "",
    ogImage: post?.cover_image || data?.header?.logo_url || null,
    ogType: "article",
  });
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ color: "var(--mv-text-muted)" }}>Lade ...</div>;
  if (!post) return <div className="p-12">Beitrag nicht gefunden.</div>;
  const heading = data.theme?.heading_font || "Playfair Display";
  return (
    <PublicShell salon={data.salon} theme={data.theme} header={data.header} footer={data.footer} planFeatures={data.plan_features || []}>
      <article className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
        <p className="mv-label mb-3">{post.category}</p>
        <h1 style={{ fontFamily: `'${heading}', serif` }} className="text-4xl md:text-6xl leading-tight mb-4">{post.title}</h1>
        <p className="text-sm opacity-70 mb-8">Von {post.author} · {post.published_at ? new Date(post.published_at).toLocaleDateString("de-DE") : ""}</p>
        {post.cover_image && <img src={post.cover_image} className="w-full rounded-2xl mb-10 max-h-[480px] object-cover" alt="" />}
        <div className="prose prose-lg max-w-none mv-blog-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || "") }} />
      </article>
    </PublicShell>
  );
}


function FooterNewsletter({ primary, heading, slug }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/public/salon/${slug}/newsletter`, { email });
      setDone(data.message || "Erfolgreich!");
    } catch { setDone("Fehler bei der Anmeldung."); }
  };
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
      <div>
        <p style={{ fontFamily: `'${heading}', serif` }} className="text-2xl mb-1">Newsletter</p>
        <p className="text-sm opacity-75">Trends, Aktionen und neue Termine direkt in Ihr Postfach.</p>
      </div>
      {done ? (
        <p className="font-medium" data-testid="footer-newsletter-done">{done}</p>
      ) : (
        <form onSubmit={submit} className="flex gap-2 max-w-md w-full md:w-auto">
          <input
            data-testid="footer-newsletter-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Ihre E-Mail"
            className="mv-input flex-1"
          />
          <button data-testid="footer-newsletter-submit" className="px-5 py-3 rounded-full text-white text-sm font-medium" style={{ background: primary }}>
            Anmelden
          </button>
        </form>
      )}
    </div>
  );
}



export function BookingPage({ salon, services, staff = [], addons = [], categories = [], staffBlocks = [], settings = {}, theme, header, footer, planFeatures = [] }) {
  const bookingAllowed = (planFeatures || []).includes("bookings");
  const { t } = usePublicTerminology();
  // Use the booking-specific SEO when present on the dedicated /booking page in
  // the CMS; otherwise fall back to a sensible auto-generated title/description.
  const bookingPage = (header && header.booking_page) || null; // future-proof for per-page override
  const salonName = (header && header.brand_name) || (salon && salon.name) || "";
  useDocumentSeo({
    title: bookingPage?.seo?.title || `Termin online buchen${salonName ? ` | ${salonName}` : ""}`,
    description: bookingPage?.seo?.description || `Buche deinen nächsten Termin bei ${salonName || "uns"} bequem online.`,
    keywords: bookingPage?.seo?.keywords || "",
    ogImage: header?.logo_url || null,
  });
  const primary = theme?.primary_color || "#C87A5F";
  const headingFont = theme?.heading_font || "Playfair Display";
  // Booking-card surface: configurable via Theme Studio so admins can fix contrast
  // in dark themes. Falls back to white card + auto-derived text colour.
  const { bg: cardBg, text: cardText, border: tileBorder, subtle: subtleSurface } = getPublicCardStyle(theme);
  const inputStyle = cardBg === "#fff"
    ? { color: "#1a1a1a" }
    : { background: subtleSurface, color: cardText, borderColor: tileBorder };
  const staffRequired = !!settings.staff_selection_required;
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  // Categories that actually have at least one active service
  const realUsableCategories = categories.filter((c) => services.some((s) => s.category_id === c.id));
  const uncategorizedServices = services.filter((s) => !s.category_id || !categories.find((c) => c.id === s.category_id));
  const UNCATEGORIZED_ID = "__uncategorized__";
  const usableCategories = realUsableCategories.length > 0 && uncategorizedServices.length > 0
    ? [...realUsableCategories, { id: UNCATEGORIZED_ID, name: "Sonstiges", icon: "Sparkles", order: 9999, active: true }]
    : realUsableCategories;
  const needsCategoryStep = usableCategories.length > 1;
  const initialServiceId = urlParams.get("service") || "";
  const initialService = services.find((s) => s.id === initialServiceId);
  const initialCategoryId = initialService
    ? (initialService.category_id && realUsableCategories.find((c) => c.id === initialService.category_id)
        ? initialService.category_id
        : (uncategorizedServices.includes(initialService) && usableCategories.some((c) => c.id === UNCATEGORIZED_ID) ? UNCATEGORIZED_ID : ""))
    : (needsCategoryStep ? "" : (usableCategories[0]?.id || ""));
  const [categoryId, setCategoryId] = useState(initialCategoryId);
  const visibleServices = categoryId === UNCATEGORIZED_ID
    ? uncategorizedServices
    : (categoryId
        ? services.filter((s) => s.category_id === categoryId)
        : (needsCategoryStep ? [] : services));
  const [serviceId, setServiceId] = useState(initialServiceId || visibleServices[0]?.id || "");
  const [addonIds, setAddonIds] = useState([]);
  const [staffId, setStaffId] = useState(urlParams.get("staff") || "");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const [loggedInCustomer, setLoggedInCustomer] = useState(null);
  const customerToken = typeof window !== "undefined" ? window.localStorage.getItem(`studiomv_customer_${salon.slug}`) : null;

  // Prefill the form when the visitor is already logged in to their salon-customer account
  useEffect(() => {
    if (!customerToken) return undefined;
    let cancelled = false;
    api.get(`/public/salon/${salon.slug}/customer/me`, { headers: { Authorization: `Bearer ${customerToken}` } })
      .then(({ data }) => {
        if (cancelled) return;
        setLoggedInCustomer(data);
        setForm((f) => ({
          ...f,
          customer_name: data.name || f.customer_name,
          customer_email: data.email || f.customer_email,
          customer_phone: data.phone || f.customer_phone,
        }));
      })
      .catch(() => { /* invalid/expired token — silently treat as anonymous */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerToken, salon.slug]);
  const [reminderOptIn, setReminderOptIn] = useState(true);
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [done, setDone] = useState("");
  const [err, setErr] = useState("");
  const [redemptionCode, setRedemptionCode] = useState("");
  const [appliedRedemption, setAppliedRedemption] = useState(null);
  const [redemptionMsg, setRedemptionMsg] = useState("");

  const service = services.find((s) => s.id === serviceId);
  const allEligibleStaff = service?.staff_ids?.length ? staff.filter((s) => service.staff_ids.includes(s.id)) : staff;
  const blockedStaffIds = date ? new Set(staffBlocks.filter((b) => b.start_date <= date && b.end_date >= date).map((b) => b.staff_id)) : new Set();
  const eligibleStaff = date ? allEligibleStaff.filter((s) => !blockedStaffIds.has(s.id)) : allEligibleStaff;
  const serviceAddons = addons.filter((a) => a.service_id === serviceId);
  const selectedAddons = serviceAddons.filter((a) => addonIds.includes(a.id));
  const totalDuration = (service?.duration_minutes || 0) + selectedAddons.reduce((s, a) => s + (a.duration_minutes || 0), 0);
  const subTotal = (service?.price || 0) + selectedAddons.reduce((s, a) => s + (a.price || 0), 0);

  let discount = 0;
  if (appliedRedemption) {
    if (appliedRedemption.reward_type === "discount_percent") {
      discount = +(subTotal * (appliedRedemption.reward_value / 100)).toFixed(2);
    } else if (appliedRedemption.reward_type === "discount_fixed") {
      discount = Math.min(subTotal, appliedRedemption.reward_value);
    } else if (appliedRedemption.reward_type === "free_service") {
      if (!appliedRedemption.service_id || appliedRedemption.service_id === serviceId) {
        discount = service?.price || 0;
      }
    }
  }
  const totalPrice = Math.max(0, subTotal - discount);

  useEffect(() => { setAddonIds([]); setTime(""); }, [serviceId]);

  // If a chosen staff is blocked on the selected date, clear the selection
  useEffect(() => {
    if (staffId && date && blockedStaffIds.has(staffId)) setStaffId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, staffId]);

  // If staff selection is required and current value is empty, auto-pick the first eligible
  useEffect(() => {
    if (staffRequired && !staffId && eligibleStaff.length > 0) {
      setStaffId(eligibleStaff[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffRequired, eligibleStaff.length, serviceId, date]);

  // When category changes, reset service to first within that category (or clear)
  useEffect(() => {
    if (!categoryId) { setServiceId(""); return; }
    const inCategory = (s) =>
      categoryId === UNCATEGORIZED_ID
        ? (!s.category_id || !categories.find((c) => c.id === s.category_id))
        : s.category_id === categoryId;
    if (!serviceId || !services.find((s) => s.id === serviceId && inCategory(s))) {
      const first = services.find(inCategory);
      setServiceId(first?.id || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => {
    if (!serviceId || !date) { setSlots([]); return; }
    setLoadingSlots(true);
    setTime("");
    const params = new URLSearchParams({ service_id: serviceId, date });
    if (staffId) params.set("staff_id", staffId);
    if (addonIds.length) params.set("addon_ids", addonIds.join(","));
    api.get(`/public/salon/${salon.slug}/slots?${params.toString()}`)
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [serviceId, date, staffId, addonIds, salon.slug]);

  if (!bookingAllowed) {
    return (
      <PublicShell salon={salon} theme={theme} header={header} footer={footer} planFeatures={planFeatures}>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display text-3xl mb-4">Online-Terminbuchung nicht verfügbar</h1>
          <p style={{ color: "var(--mv-text-muted)" }}>
            Dieser Salon bietet aktuell keine Online-Terminbuchung an. Bitte kontaktiere den Salon direkt, um einen Termin zu vereinbaren.
          </p>
        </div>
      </PublicShell>
    );
  }

  const toggleAddon = (aid) => {
    setAddonIds(addonIds.includes(aid) ? addonIds.filter((x) => x !== aid) : [...addonIds, aid]);
  };

  const applyCode = async () => {
    setRedemptionMsg(""); setErr("");
    const code = redemptionCode.trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await api.get(`/public/salon/${salon.slug}/redemption/${code}`);
      if (data.reward_type === "free_service" && data.service_id && data.service_id !== serviceId) {
        setRedemptionMsg("Diese Belohnung gilt für eine andere Leistung.");
        return;
      }
      setAppliedRedemption(data);
      setRedemptionMsg(`Code angewendet: ${data.reward_name}`);
    } catch (e2) {
      setAppliedRedemption(null);
      setRedemptionMsg(e2.response?.data?.detail || "Code ungültig");
    }
  };

  const clearCode = () => { setAppliedRedemption(null); setRedemptionCode(""); setRedemptionMsg(""); };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (staffRequired && !staffId) { setErr("Bitte einen Mitarbeiter auswählen"); return; }
    if (!time) { setErr("Bitte Uhrzeit wählen"); return; }
    if (!consentPrivacy) { setErr("Bitte Datenschutz & AGB bestätigen"); return; }
    try {
      const { data } = await api.post(`/public/salon/${salon.slug}/book`, {
        service_id: serviceId, staff_id: staffId || null, addon_ids: addonIds,
        date, time, ...form,
        redemption_code: appliedRedemption ? appliedRedemption.code : null,
        reminder_opt_in: reminderOptIn,
        consent_privacy: consentPrivacy,
      }, customerToken ? { headers: { Authorization: `Bearer ${customerToken}` } } : undefined);
      setDone(data.message);
    } catch (e2) { setErr(e2.response?.data?.detail || "Buchung fehlgeschlagen"); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <PublicShell salon={salon} theme={theme} header={header} footer={footer} planFeatures={planFeatures}>
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
        <p className="mv-label mb-2">Online buchen</p>
        <h1 style={{ fontFamily: `'${headingFont}', serif` }} className="text-4xl md:text-6xl leading-tight mb-4">{t("book_appointment")}</h1>
        <p className="text-sm md:text-base opacity-75 mb-10">In wenigen Schritten zum Wunschtermin.</p>

        {done ? (
          <div className="p-10 rounded-2xl text-center" style={{ background: "var(--mv-surface)" }}>
            <p style={{ fontFamily: `'${headingFont}', serif` }} className="text-3xl mb-2">{t("appointment")} angefragt</p>
            <p className="opacity-75">{done}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6 p-6 md:p-10 rounded-2xl border" style={{ borderColor: tileBorder, background: cardBg, color: cardText }} data-testid="booking-form-card">
            {needsCategoryStep && (
              <div data-testid="category-step">
                <p className="mv-label mb-3">1. Bereich wählen</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {usableCategories.map((c) => {
                    const Icon = LucideIcons[c.icon] || LucideIcons.Scissors;
                    const sel = categoryId === c.id;
                    const count = c.id === UNCATEGORIZED_ID
                      ? uncategorizedServices.length
                      : services.filter((s) => s.category_id === c.id).length;
                    return (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => setCategoryId(c.id)}
                        data-testid={`pick-category-${c.id}`}
                        className="p-5 rounded-xl border text-left transition-colors flex items-center gap-3"
                        style={{ borderColor: sel ? primary : tileBorder, background: sel ? `${primary}10` : "transparent", color: cardText }}
                      >
                        <div
                          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: sel ? primary : `${primary}18`, color: sel ? "#fff" : primary }}
                        >
                          <Icon className="w-5 h-5" strokeWidth={1.6} />
                        </div>
                        <div>
                          <p className="font-medium">{c.name}</p>
                          <p className="text-xs opacity-70">{count} {t("service_plural")}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {(!needsCategoryStep || categoryId) && (
              <div>
                <p className="mv-label mb-3">{needsCategoryStep ? "2." : "1."} {t("select_service")}</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {visibleServices.map((s) => (
                    <label key={s.id} className="p-4 rounded-xl border cursor-pointer transition-colors" style={{ borderColor: serviceId === s.id ? primary : tileBorder, background: serviceId === s.id ? `${primary}10` : "transparent", color: cardText }} data-testid={`pick-service-${s.id}`}>
                      <input type="radio" className="hidden" name="srv" checked={serviceId === s.id} onChange={() => setServiceId(s.id)} />
                      <p className="font-medium">{s.name}</p>
                      <p className="text-xs opacity-75">{s.duration_minutes} Min · {s.price?.toFixed(2)} €</p>
                    </label>
                  ))}
                </div>
                {visibleServices.length === 0 && (
                  <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>Keine {t("service_plural")} in dieser Kategorie verfügbar.</p>
                )}
              </div>
            )}

            {serviceAddons.length > 0 && (
              <div>
                <p className="mv-label mb-3">{needsCategoryStep ? "3." : "2."} {t("addon_plural")} (optional)</p>
                <div className="space-y-2">
                  {serviceAddons.map((a) => {
                    const sel = addonIds.includes(a.id);
                    return (
                      <label key={a.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer" style={{ borderColor: sel ? primary : tileBorder, background: sel ? `${primary}10` : "transparent", color: cardText }} data-testid={`addon-${a.id}`}>
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={sel} onChange={() => toggleAddon(a.id)} />
                          <div>
                            <p className="font-medium text-sm">{a.name}</p>
                            {a.description && <p className="text-xs opacity-60">{a.description}</p>}
                          </div>
                        </div>
                        <p className="text-xs whitespace-nowrap">+{a.duration_minutes} Min · +{a.price?.toFixed(2)} €</p>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {(service || selectedAddons.length > 0) && (
              <div className="space-y-2">
                <div>
                  <p className="mv-label mb-2">Treuepunkte-Code (optional)</p>
                  <div className="flex gap-2">
                    <input
                      data-testid="redemption-code-input"
                      className="mv-input flex-1"
                      placeholder="LV-XXXXXXXX"
                      value={redemptionCode}
                      onChange={(e) => setRedemptionCode(e.target.value.toUpperCase())}
                      disabled={!!appliedRedemption}
                      style={inputStyle}
                    />
                    {appliedRedemption ? (
                      <button type="button" data-testid="redemption-clear" onClick={clearCode} className="mv-btn-ghost text-sm">Entfernen</button>
                    ) : (
                      <button type="button" data-testid="redemption-apply" onClick={applyCode} className="mv-btn-ghost text-sm">Einlösen</button>
                    )}
                  </div>
                  {redemptionMsg && (
                    <p className="text-xs mt-2" style={{ color: appliedRedemption ? primary : "#b91c1c" }} data-testid="redemption-msg">{redemptionMsg}</p>
                  )}
                </div>
                <div className="text-sm p-3 rounded-lg space-y-1" style={{ background: "var(--mv-surface)" }}>
                  <div className="flex justify-between"><span className="opacity-75">Zwischensumme</span><span>{subTotal.toFixed(2)} €</span></div>
                  {discount > 0 && (
                    <div className="flex justify-between" style={{ color: primary }}><span>Rabatt ({appliedRedemption.reward_name})</span><span>−{discount.toFixed(2)} €</span></div>
                  )}
                  <div className="flex justify-between font-medium pt-1 border-t" style={{ borderColor: "var(--mv-border)" }}>
                    <span>Gesamt</span><span data-testid="booking-total">{totalDuration} Min · {totalPrice.toFixed(2)} €</span>
                  </div>
                </div>
              </div>
            )}

            {eligibleStaff.length > 0 && (
              <div>
                <p className="mv-label mb-3">{t("stylist")} {staffRequired ? "(Pflicht)" : "(optional)"}</p>
                <div className="flex flex-wrap gap-2">
                  {!staffRequired && (
                    <button type="button" data-testid="pick-staff-any" onClick={() => setStaffId("")} className="px-4 py-2 rounded-full text-sm border" style={{ background: !staffId ? primary : "transparent", color: !staffId ? "#fff" : cardText, borderColor: !staffId ? primary : tileBorder }}>Egal</button>
                  )}
                  {eligibleStaff.map((s) => {
                    const sel = staffId === s.id;
                    return <button type="button" key={s.id} onClick={() => setStaffId(s.id)} data-testid={`pick-staff-${s.id}`} className="px-4 py-2 rounded-full text-sm border" style={{ background: sel ? primary : "transparent", color: sel ? "#fff" : cardText, borderColor: sel ? primary : tileBorder }}>{s.name}</button>;
                  })}
                </div>
              </div>
            )}

            <div>
              <p className="mv-label mb-3">Datum</p>
              <input type="date" required className="mv-input" value={date} min={today} onChange={(e) => setDate(e.target.value)} data-testid="bookingpage-date" style={inputStyle} />
            </div>

            {date && (
              <div>
                <p className="mv-label mb-3">Uhrzeit</p>
                {loadingSlots ? (
                  <p className="text-sm opacity-70">Lade verfügbare Zeiten ...</p>
                ) : slots.length === 0 ? (
                  <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>Keine {t("appointment_plural")} an diesem Tag verfügbar. Bitte anderes Datum wählen.</p>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="slot-grid">
                    {slots.map((slot) => {
                      const sel = time === slot;
                      return (
                        <button type="button" key={slot} onClick={() => setTime(slot)} data-testid={`slot-${slot}`} className="px-3.5 py-2 rounded-lg text-sm border transition-colors" style={{ background: sel ? primary : "transparent", color: sel ? "#fff" : cardText, borderColor: sel ? primary : tileBorder }}>
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {time && (
              <div className="space-y-3 pt-2 border-t" style={{ borderColor: "var(--mv-border)" }}>
                <p className="mv-label mt-4">Ihre Daten</p>
                {loggedInCustomer && (
                  <div
                    className="p-3 rounded-md flex items-center gap-2 text-sm"
                    style={{ background: "var(--mv-surface)", borderLeft: `3px solid ${primary}` }}
                    data-testid="bookingpage-logged-in-banner"
                  >
                    <span style={{ color: primary }}>✓</span>
                    <span>Angemeldet als <strong>{loggedInCustomer.name || loggedInCustomer.email}</strong> — deine Daten sind vorausgefüllt.</span>
                  </div>
                )}
                <div><label className="block text-xs font-medium mb-1.5">Name</label><input required className="mv-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="bookingpage-name" style={inputStyle} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1.5">E-Mail</label>
                    <input
                      required
                      type="email"
                      className="mv-input"
                      value={form.customer_email}
                      onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                      readOnly={!!loggedInCustomer}
                      style={loggedInCustomer ? { ...inputStyle, background: subtleSurface, cursor: "not-allowed" } : inputStyle}
                      data-testid="bookingpage-email"
                    />
                  </div>
                  <div><label className="block text-xs font-medium mb-1.5">Telefon</label><input className="mv-input" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} style={inputStyle} /></div>
                </div>
                <div><label className="block text-xs font-medium mb-1.5">Anmerkung (optional)</label><textarea className="mv-input min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={inputStyle} /></div>

                {/* Reminder opt-in */}
                <label className="flex items-start gap-3 cursor-pointer select-none rounded-lg p-3 border" style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface)" }} data-testid="bookingpage-reminder-row">
                  <input
                    type="checkbox"
                    checked={reminderOptIn}
                    onChange={(e) => setReminderOptIn(e.target.checked)}
                    className="mt-0.5 h-4 w-4 cursor-pointer"
                    style={{ accentColor: primary }}
                    data-testid="bookingpage-reminder-toggle"
                  />
                  <span className="text-sm">
                    <span className="font-medium">{t("reminder_optin")}</span>
                    <span className="block text-xs opacity-70 mt-0.5">Wir senden Ihnen kurz vor dem {t("appointment")} eine kurze Erinnerung – jederzeit abbestellbar.</span>
                  </span>
                </label>

                {/* Privacy + AGB consent (required) */}
                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="bookingpage-consent-row">
                  <input
                    type="checkbox"
                    required
                    checked={consentPrivacy}
                    onChange={(e) => setConsentPrivacy(e.target.checked)}
                    className="mt-0.5 h-4 w-4 cursor-pointer"
                    style={{ accentColor: primary }}
                    data-testid="bookingpage-consent-checkbox"
                  />
                  <span className="text-sm">
                    Ich habe die{" "}
                    <a
                      href={`/salon/${salon.slug}/datenschutz`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      style={{ color: primary }}
                      data-testid="bookingpage-privacy-link"
                    >
                      Datenschutzerklärung
                    </a>{" "}
                    und{" "}
                    <a
                      href={`/salon/${salon.slug}/agb`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                      style={{ color: primary }}
                      data-testid="bookingpage-terms-link"
                    >
                      AGB
                    </a>{" "}
                    gelesen und akzeptiere sie.
                    <span className="ml-0.5" aria-hidden="true" style={{ color: "#dc2626" }}>*</span>
                  </span>
                </label>

                {err && <p className="text-sm text-red-500">{err}</p>}
                <button data-testid="bookingpage-submit" className="px-6 py-3 rounded-full text-white font-medium disabled:opacity-50" style={{ background: primary }} disabled={!consentPrivacy}>{t("request_appointment")}</button>
              </div>
            )}
          </form>
        )}
      </div>
    </PublicShell>
  );
}

